<script lang="ts" module>
  declare let window: any
  declare var Stripe: any
</script>

<script lang="ts">
  import { onMount } from "svelte"
  import { Loader } from "lucide-svelte"
  import Layout from "$app/Layouts/Simple.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import {
    NotebookText,
    Lightbulb,
    CircleArrowRight,
    GraduationCap,
    MessageCircleQuestion,
    ShieldAlert
  } from "lucide-svelte"
  import { formatter, xhr, toast } from "$app/utils"

  import hero_bg_img from "@images/alignment_test/rowing.jpg"
  import team from "@images/alignment_test/team.jpg"

  import business_logo1 from "@images/companies/cba.png"
  import business_logo2 from "@images/companies/cfg-logo.png"
  import business_logo3 from "@images/companies/accutrans.png"
  import business_logo4 from "@images/companies/classic-plan.png"
  import business_logo5 from "@images/companies/horizon-hobby.png"
  import business_logo6 from "@images/companies/prevost.png"

  let props = $props()
  let stripe_publish_key: string = props.stripe_publish_key
  let recaptcha_site_key_v3: string = props.recaptcha_site_key_v3
  let stripe_product: any = props.stripe_product
  let errors: any = $state(props.errors || {})
  let params: any = props.params || {}

  let form: HTMLFormElement
  let stripe_token: string = $state("")
  let cardErrors: any
  let cardTokenInput: string
  let saving = $state(false)
  let mounted = false
  let auth_token: string = $state("")
  let recaptcha_token: string = $state("")

  let productPrice: number = $state(stripe_product.unit_amount / 100)
  let discountAmount: number = $state(0)
  let totalAmount: number = $derived(productPrice)

  let submitTimes = 0
  function handleSubmit(): void {
    window.grecaptcha
      .execute(recaptcha_site_key_v3, { action: "signup" })
      .then((token) => {
        // To test if the recaptcha submits again
        // appStore.form["g-recaptcha-response-data"] = token + 'FAIL'

        recaptcha_token = token
        submitTimes++
        submitForm()
      })
  }

  async function submitForm(): Promise<void> {
    saving = true

    // Attempt to tokenize the card number
    try {
      let result: any = { token: { id: "" } }
      result = await stripe.createToken(cardElement)

      if (result.error) {
        // On error, display the error message.
        cardErrors.classList.remove("hidden")
        cardErrors.textContent = result.error.message
        throw new Error("Card error")
      } else {
        // On success, add the token to the hidden field and submit to the server
        stripe_token = result.token.id

        setTimeout(() => {
          saving = true
          form.submit()
        }, 50)
      }
    } catch (e) {
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
    }
  }

  let stripe: any
  let cardElement: any

  const clearCardError = () => {
    cardErrors.classList.add("hidden")
  }

  /**
   * Stripe
   */
  function loadStripeElements() {
    // Time for Stripe.js to do its magic.
    stripe = Stripe(stripe_publish_key)
    let elements = stripe.elements()

    let style = {
      base: {
        fontSize: "16px",
        color: "#32325d",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
        fontSmoothing: "antialiased",
        "::placeholder": {
          color: "#a0aec0"
        }
      }
    }

    cardElement = elements.create("card", { style: style })
    cardElement.mount("#card-element")
    cardElement.on("change", clearCardError)
  }

  onMount(async () => {
    auth_token = (<HTMLMetaElement>(
      document.querySelector("meta[name=csrf-token]")
    )).content

    mounted = true

    if (stripe_coupon && !checked_coupon) {
      checkCoupon()
    }
  })

  let validate_btn
  let stripe_coupon: string = $state("")
  let validating = $state(false)
  let checked_coupon = $state(false)

  let coupon = $state({})

  /**
   * Review valid coupon
   */
  async function checkCoupon(): Promise<void> {
    stripe_coupon = stripe_coupon?.trim()

    if (stripe_coupon?.length > 0) {
      checked_coupon = true
      validating = true

      try {
        const resp = await xhr.get(
          `/stripe/promotion_code/${stripe_coupon.trim()}`
        )
        let promotion_code = resp.data

        if (promotion_code.valid) {
          coupon = promotion_code.coupon
          discountAmount = (productPrice * (coupon.percent_off || 0)) / 100
          totalAmount = productPrice - discountAmount
        } else {
          coupon.valid = false
        }

        console.log(promotion_code)
      } catch (e) {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
        toast(
          "There was an error validating the coupon code try again",
          "error"
        )
      } finally {
        validating = false
      }
    } else {
      coupon = {}
    }
  }

  function focusCoupon() {
    validate_btn.classList.remove("btn-secondary")
    validate_btn.classList.add("btn-primary")
  }

  function stripeLoaded(): void {
    // The external Stripe javascript is ready.
    // stripeReady = true
    const interval = setInterval(() => {
      if (mounted) {
        loadStripeElements()
        clearInterval(interval)
      }
    }, 200)
  }
</script>

<svelte:head>
  <script
    src="https://www.google.com/recaptcha/api.js?render={recaptcha_site_key_v3}"
    async
    defer
  ></script>
  <script src="https://js.stripe.com/v3/" onload={stripeLoaded}></script>
</svelte:head>

<Layout {...props}>
  <div class="relative bg-lwp-theme-blue">
    <div class="mx-auto max-w-7xl lg:grid lg:grid-cols-12 lg:gap-x-8 lg:px-8">
      <div
        class="px-6 pb-24 pt-10 sm:pb-32 lg:col-span-6 lg:px-0 lg:pb-56 lg:pt-32 xl:col-span-6"
      >
        <div class="mx-auto max-w-2xl lg:mx-0">
          <h1
            class="mt-24 text-4xl font-bold tracking-tight text-amber-300 sm:mt-10 sm:text-6xl uppercase text-right"
          >
            Is Your Leadership Team Rowing in Different Directions?
          </h1>
        </div>
      </div>
      <div
        class="relative lg:col-span-6 lg:-mr-8 xl:absolute xl:inset-0 xl:left-1/2 xl:mr-0"
      >
        <img
          class="aspect-[3/2] w-full bg-gray-50 object-cover lg:absolute lg:inset-0 lg:aspect-auto lg:h-full"
          src={hero_bg_img}
          alt=""
        />
      </div>
    </div>
  </div>

  <div class="bg-gray-50 px-4 md:px-8">
    <div
      class="py-6 lg:py-12 mx-auto max-w-md px-4 sm:max-w-2xl md:px-0 lg:max-w-4xl"
    >
      <h1
        class="text-center text-black text-lg sm:text-xl md:text-2xl lg:text-3xl"
      >
        <div class="block mt-6">
          <span
            class="bg-red-950 text-white font-bold rounded-sm px-2 py-1 italic"
            >Misalignment is a silent killer.</span
          > It shows up as missed deadlines, conflicting priorities, disengaged and
          unproductive employees, constant firefighting, and an exhausted leader
          who feels like they’re carrying the entire company on their shoulders.
        </div>
      </h1>
    </div>
    <div
      class="py-4 lg:py-10 mx-auto max-w-md px-4 sm:max-w-3xl sm:px-0 lg:max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4"
    >
      <div class="bg-white rounded-l-lg lg:rounded-l-4xl shadow">
        <div class="py-8 px-6 lg:px-8">
          <p class="text-lg/8 text-gray-700 md:text-xl">
            If you’ve ever wondered:
          </p>
          <dl
            class="mt-4 max-w-xl space-y-6 text-base/7 text-gray-600 lg:max-w-none dark:text-gray-400"
          >
            <div class="relative pl-4 flex items-center justify-start">
              <dt class="inline font-semibold text-gray-900 mr-3">
                <MessageCircleQuestion class="size-8 text-blue-800" />
              </dt>
              <dd class="italic">
                “Why do I have to be involved in every single decision?”
              </dd>
            </div>
            <div class="relative pl-4 flex items-center justify-start">
              <dt class="inline font-semibold text-gray-900 mr-3">
                <MessageCircleQuestion class="size-8 text-blue-800" />
              </dt>
              <dd class="italic">“Why don’t my leaders take ownership?”</dd>
            </div>
            <div class="relative pl-4 flex items-center justify-start">
              <dt class="inline font-semibold text-gray-900 mr-3">
                <MessageCircleQuestion class="size-8 text-blue-800" />
              </dt>
              <dd class="italic">
                “Why does it feel like we’re rowing in different directions?”
              </dd>
            </div>
          </dl>
          <p
            class="mt-6 text-xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-xl dark:text-white"
          >
            Then you already know the cost of misalignment.
          </p>
        </div>
      </div>
      <div class="bg-white rounded-r-lg lg:rounded-r-4xl shadow">
        <div class="py-8 px-6 lg:px-8">
          <p class="text-lg/8 text-amber-900 md:text-xl">
            When your team isn’t aligned:
          </p>
          <dl
            class="mt-4 max-w-xl space-y-6 text-base/7 text-gray-600 lg:max-w-none dark:text-gray-400"
          >
            <div class="relative pl-4 flex items-center justify-start">
              <dt class="inline font-semibold text-gray-900 mr-3">
                <ShieldAlert class="size-8 text-amber-800" />
              </dt>
              <dd class="italic">Growth slows down</dd>
            </div>
            <div class="relative pl-4 flex items-center justify-start">
              <dt class="inline font-semibold text-gray-900 mr-3">
                <ShieldAlert class="size-8 text-amber-800" />
              </dt>
              <dd class="italic">Stress piles up</dd>
            </div>
            <div class="relative pl-4 flex items-center justify-start">
              <dt class="inline font-semibold text-gray-900 mr-3">
                <ShieldAlert class="size-8 text-amber-800" />
              </dt>
              <dd class="italic">
                The business becomes completely dependent on you
              </dd>
            </div>
          </dl>
          <p
            class="mt-8 text-xl font-semibold tracking-tight text-pretty text-gray-900 italic"
          >
            Alignment doesn’t happen by accident. You have to measure it first.
          </p>
        </div>
      </div>
    </div>

    <div class="mt-6 md:mt-8">
      <div class="mx-auto max-w-4xl px-2 lg:px-0">
        <div
          class="mx-auto grid max-w-2xl auto-rows-fr grid-cols-1 gap-8 lg:mx-0 lg:max-w-none"
        >
          <article
            class="relative isolate flex flex-col justify-start overflow-hidden rounded-2xl bg-lwp-theme-blue px-8 pb-8 py-8"
          >
            <div
              class="absolute inset-0 -z-10 bg-gradient-to-t from-gray-900 via-gray-900/40"
            ></div>
            <div
              class="absolute inset-0 -z-10 rounded-2xl ring-1 ring-inset ring-gray-900/10"
            ></div>

            <div
              class="flex flex-wrap items-center gap-y-1 overflow-hidden text-2xl md:text-3xl text-gray-100 font-bold"
            >
              Step 1: Discover Your Alignment Score
            </div>
            <h3 class="mt-3 text-lg leading-6 text-white">
              <p>Before you can fix a problem, you need to know the truth.</p>
              <p class="mt-4">
                The <span class="font-bold">Alignment Test</span> gives you a clear,
                data-driven score on how aligned your leadership team really is.
              </p>
              <p class="mt-4 text-base">Here’s how it works:</p>
              <ol class="list-inside list-decimal ml-4 mt-2 text-base">
                <li class="mt-2">
                  You invite your entire leadership team to take a short,
                  automated test (6 critical questions each).
                </li>
                <li class="mt-2">
                  The test calculates your team’s <span class="font-bold"
                    >Alignment Score</span
                  > - revealing blind spots and fractures you can’t see from the
                  top.
                </li>
                <li class="mt-2">
                  You receive a report that shows exactly where your team is
                  breaking down and what it will take to get them rowing in the
                  same direction.
                </li>
              </ol>
              <p class="mt-4 text-lg md:text-xl">
                <span class="font-bold">Cost of misalignment:</span> weeks of wasted
                effort, endless frustration, and a business that relies solely on
                you.
              </p>
              <p class="mt-4 text-lg md:text-xl">
                <span class="font-bold">Cost of the test:</span> $499.
              </p>
            </h3>
          </article>
        </div>
        <div
          class="mt-6 mx-auto grid max-w-2xl auto-rows-fr grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-2"
        >
          <div
            class="relative isolate flex flex-col justify-start overflow-hidden rounded-2xl bg-lwp-theme-blue px-8 pb-8 py-8"
          >
            <div
              class="absolute inset-0 -z-10 bg-gradient-to-t from-gray-900 via-gray-900/40"
            ></div>
            <div
              class="absolute inset-0 -z-10 rounded-2xl ring-1 ring-inset ring-gray-900/10"
            ></div>

            <div
              class="flex flex-wrap items-center gap-y-1 overflow-hidden text-2xl md:text-3xl text-gray-100 font-bold"
            >
              Step 2: Face the Gaps
            </div>
            <div class="mt-6 text-lg leading-6 text-white">
              <p>
                Your score will expose where your team is falling out of sync:
              </p>
              <ul class="list-disc ml-8 mt-4">
                <li class="mt-2">Are decisions bottlenecking at the top?</li>
                <li class="mt-2">
                  Are priorities clear—or shifting every week?
                </li>
                <li class="mt-2">
                  Do your leaders truly feel ownership of the mission—or are
                  they just doing tasks?
                </li>
              </ul>
              <p class="mt-4">Until you see the gaps, you can’t fix them.</p>
            </div>
          </div>

          <div
            class="relative isolate flex flex-col justify-start overflow-hidden rounded-2xl bg-lwp-theme-blue px-8 pb-8 py-8"
          >
            <div
              class="absolute inset-0 -z-10 bg-gradient-to-t from-gray-900 via-gray-900/40"
            ></div>
            <div
              class="absolute inset-0 -z-10 rounded-2xl ring-1 ring-inset ring-gray-900/10"
            ></div>

            <div
              class="flex flex-wrap items-center gap-y-1 overflow-hidden text-2xl md:text-3xl text-gray-100 font-bold"
            >
              Step 3: Take Back Control of Your Business
            </div>
            <div class="mt-6 text-lg leading-6 text-white">
              <p class="text-base">
                Once you know your score, you can take the first step toward
                alignment—and a team that moves with speed and confidence
                without you in every conversation.
              </p>
              <p class="mt-4">You’ll finally have:</p>
              <ul class="list-disc ml-8 mt-2">
                <li>A clear picture of where your leadership team stands</li>
                <li class="mt-2">
                  The insight to start removing yourself from the daily
                  firefighting
                </li>
                <li class="mt-2">
                  A path to turn stress and chaos into clarity and control
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="relative mt-12">
      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="max-w-lg mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none lg:flex"
        >
          <div class="bg-lwp-theme-blue px-6 py-8 lg:w-1/2 lg:p-12">
            <h3 class="text-2xl leading-8 text-white sm:text-3xl sm:leading-9">
              What You Get for $499
            </h3>
            <div class="mt-8">
              <div class="flex items-center">
                <div class="flex-1 border-t-2 border-blue-200"></div>
              </div>
              <ul class="mt-10">
                <li>
                  <div class="flex items-center">
                    <div class="flex-shrink-0">
                      <div
                        class="flex items-center justify-center h-12 w-12 rounded-md bg-amber-500 text-white"
                      >
                        <NotebookText class="text-amber-800 w-8 h-8" />
                      </div>
                    </div>
                    <div class="ml-4">
                      <h5
                        class="text-base md:text-xl leading-6 font-medium text-gray-100"
                      >
                        Alignment Test for your entire leadership team
                      </h5>
                    </div>
                  </div>
                </li>
                <li class="mt-4">
                  <div class="flex items-center">
                    <div class="flex-shrink-0">
                      <div
                        class="flex items-center justify-center h-12 w-12 rounded-md bg-amber-500 text-white"
                      >
                        <GraduationCap class="text-amber-800 w-8 h-8" />
                      </div>
                    </div>
                    <div class="ml-4">
                      <h5
                        class="text-base md:text-xl leading-6 font-medium text-gray-100"
                      >
                        Automated scoring and personalized results report
                      </h5>
                    </div>
                  </div>
                </li>
                <li class="mt-4">
                  <div class="flex items-center">
                    <div class="flex-shrink-0">
                      <div
                        class="flex items-center justify-center h-12 w-12 rounded-md bg-amber-500 text-white"
                      >
                        <Lightbulb class="text-amber-800 w-8 h-8" />
                      </div>
                    </div>
                    <div class="ml-4">
                      <h5
                        class="text-base md:text-xl leading-6 font-medium text-gray-100"
                      >
                        A clear view of the gaps causing frustration and
                        slowdown
                      </h5>
                    </div>
                  </div>
                </li>
                <li class="mt-4">
                  <div class="flex items-center">
                    <div class="flex-shrink-0">
                      <div
                        class="flex items-center justify-center h-12 w-12 rounded-md bg-amber-500 text-white"
                      >
                        <CircleArrowRight class="text-amber-800 w-8 h-8" />
                      </div>
                    </div>
                    <div class="ml-4">
                      <h5
                        class="text-base md:text-xl leading-6 font-medium text-gray-100"
                      >
                        Next steps to start unifying your team and reclaiming
                        your time
                      </h5>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <div class="flex items-center mt-12">
              <h4
                class="md:flex-shrink-0 md:pr-4 text-sm leading-5 tracking-wider font-semibold uppercase text-blue-100"
              >
                The longer you wait, the more it costs you.
              </h4>
              <div class="flex-1 border-t-2 border-blue-200"></div>
            </div>
            <div class="flex mt-8">
              <div class="flex-grow">
                <p class="text-lg leading-6 text-white">
                  Every week you lead with misalignment is a week of wasted
                  effort, missed opportunities, and decision fatigue.
                </p>
              </div>
            </div>
            <div class="mt-8">
              <p class="text-lg text-white">
                For a <span class="font-bold">one-time payment of $499</span>,
                you can uncover the truth, identify the cracks, and finally
                start building a team that leads with you—not behind you.
              </p>
            </div>
          </div>
          <div
            class="py-8 px-6 text-center bg-gray-100 lg:w-1/2 lg:flex lg:flex-col lg:justify-center lg:p-8"
          >
            <div>
              <div class="rounded-md text-left">
                <h2
                  class="text-lg md:text-2xl leading-9 font-extrabold text-gray-800 text-center"
                >
                  Sign up now and get your Alignment Test
                </h2>

                <form
                  onsubmit={(e) => {
                    e.preventDefault()
                    handleSubmit()
                  }}
                  method="post"
                  class="mt-8"
                  action="/alignment_signup"
                  bind:this={form}
                  id="payment-form"
                >
                  {#if Object.keys(errors).length > 0}
                    <div class="bg-red-600 text-white text-sm p-3 mb-2">
                      There are some errors in the form that prohibited to
                      create the account, please correct the errors shown below:
                    </div>
                  {/if}

                  {#if errors.recaptcha}
                    <div class="text-red-600 text-center font-semibold my-4">
                      {errors.recaptcha}
                    </div>
                  {/if}

                  <input
                    type="hidden"
                    name="purchase[stripe_product_id]"
                    value={stripe_product.stripe_id}
                  />
                  {#if coupon.id}
                    <input
                      type="hidden"
                      name="purchase[stripe_coupon]"
                      value={coupon.id}
                    />
                  {/if}
                  <input
                    type="hidden"
                    name="purchase[stripe_price_id]"
                    value={stripe_product.price_id}
                  />

                  <input
                    type="hidden"
                    name="purchase[stripe_token]"
                    value={stripe_token}
                  />

                  <input
                    name="authenticity_token"
                    type="hidden"
                    bind:value={auth_token}
                  />

                  <input
                    type="hidden"
                    name="g-recaptcha-response-data"
                    bind:value={recaptcha_token}
                  />

                  <div class="input-cont" class:error={errors.email}>
                    <label
                      for="user[email]"
                      class="block text-sm font-medium text-gray-700"
                    >
                      Email address
                    </label>
                    <div class="mt-1">
                      <input
                        id="email"
                        name="user[email]"
                        type="email"
                        required
                        class="input rounded-md"
                        value={params?.user?.email ? params.user.email : ""}
                      />
                    </div>
                    <div class="msg">{errors.email}</div>
                  </div>

                  <div class="input-cont mt-4" class:error={errors.name}>
                    <label
                      for="user[name]"
                      class="block text-sm font-medium text-gray-700"
                    >
                      Your full name
                    </label>
                    <div class="mt-1">
                      <input
                        id="full_name"
                        name="user[name]"
                        type="text"
                        required
                        class="input rounded-md"
                        value={params?.user?.name ? params.user.name : ""}
                      />
                    </div>
                    <div class="msg">{errors.name}</div>
                  </div>

                  <div class="input-cont mt-4" class:error={errors.password}>
                    <label
                      for="user[password]"
                      class="block text-sm font-medium text-gray-700"
                    >
                      Password
                    </label>
                    <div class="mt-1">
                      <input
                        id="password"
                        name="user[password]"
                        type="password"
                        autocomplete="current-password"
                        required
                        class="input rounded-md"
                      />
                    </div>
                    <div class="msg">{errors.password}</div>
                  </div>

                  <div class="space-y-2 mt-6">
                    <h3
                      class="text-2xl font-extrabold tracking-tight mt-2 text-gray-600"
                    >
                      Payment Details
                    </h3>

                    <div class="mt-4 flex justify-between py-2">
                      <div class="text-left text-lg">
                        <p>Alignment Test</p>
                      </div>
                      <div class="text-right text-lg font-bold">
                        <p>{formatter.format(productPrice)} USD</p>
                      </div>
                    </div>

                    {#if discountAmount}
                      <div class="mt-4 flex justify-between py-2">
                        <div class="text-left text-base italic text-grey-700">
                          <p>Discount</p>
                        </div>
                        <div
                          class="text-right text-base italic text-gray-700 font-bold"
                        >
                          <p>-{formatter.format(discountAmount)}</p>
                        </div>
                      </div>
                    {/if}
                    <div
                      class="mt-4 mb-4 py-2 flex justify-between border-t border-dotted border-gray-400"
                    >
                      <div class="text-left text-xl">
                        <p>Your Total</p>
                      </div>
                      <div class="text-right text-xl font-bold">
                        <p>{formatter.format(totalAmount)} USD</p>
                      </div>
                    </div>

                    <div class="mt-4 grid grid-cols-1 gap-y-4 gap-x-4">
                      <div>
                        <div class="flex coupon-label">
                          {#if coupon.valid === true}
                            <div class="text-green-600 text-right w-full">
                              Valid Coupon
                            </div>
                          {:else if coupon.valid === false}
                            <div class="text-red-600 text-right w-full">
                              Invalid Coupon
                            </div>
                          {/if}
                        </div>
                        <div class="flex mt-1">
                          <input
                            type="text"
                            name="subscription[stripe_coupon]"
                            bind:value={stripe_coupon}
                            class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md rounded-r-none px-2 py-1 bg-white"
                            class:border-red-600={coupon.valid === false}
                            class:border-green-600={coupon.valid === true}
                            placeholder="Coupon code"
                            onblur={checkCoupon}
                            onfocus={focusCoupon}
                          />
                          <button
                            onclick={(e) => {
                              e.preventDefault()
                              checkCoupon()
                            }}
                            bind:this={validate_btn}
                            class="btn btn-secondary whitespace-nowrap !rounded-l-none"
                            disabled={validating}
                          >
                            Validate Coupon
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class:hidden={totalAmount == 0}>
                      <!-- We'll put the error messages in this element -->
                      <div bind:this={cardErrors} role="alert"></div>
                      <div class="mt-4 grid grid-cols-1 gap-y-4 gap-x-4">
                        <div class="pb-2">
                          <label
                            for="first_name"
                            class="block text-sm font-medium text-gray-700"
                          >
                            Credit Card
                          </label>
                          <div
                            class="mt-1 p-2 border shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white"
                          >
                            <div id="card-element" class=""></div>
                            <input type="hidden" bind:this={cardTokenInput} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="mt-6">
                    <button
                      type="submit"
                      disabled={saving || coupon.valid === false}
                      class="btn big bg-lwp-blue hover:bg-blue-700 text-white inline font-bold text-center w-full justify-center"
                    >
                      {#if saving}
                        <Loader class="animate-spin" />
                        Please wait...
                      {:else}
                        Purchase Alignment Test
                      {/if}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="bg-white">
    <div class="max-w-7xl mx-auto py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
      <h2 class="text-3xl lg:text-4xl font-extrabold text-black text-center">
        Trusted by Thousands of Businesses
      </h2>

      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mt-10">
        <div class="flex justify-center items-center">
          <img src={business_logo1} class="mx-auto max-h-16 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo2} class="mx-auto max-h-16 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo3} class="mx-auto max-h-16 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo4} class="mx-auto max-h-12 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo5} class="mx-auto max-h-10 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo6} class="mx-auto max-h-16 w-auto" alt="" />
        </div>
      </div>
    </div>
  </div>

  <div class="relative mt-12">
    <div class="max-h-128 overflow-hidden">
      <img
        src={team}
        class="w-full h-full object-center"
        alt="Alignment Test"
      />
    </div>
  </div>
</Layout>
